import {
  ACCESS_DENIED_ERROR,
  INTERNAL_SERVER_ERROR,
  UNAUTHORIZED_ERROR,
} from '@whistler-ui/k2/constants';
import { removeQuotesFromText } from '@whistler-ui/k2/helpers';
import { HttpError } from '@whistler-ui/k2/types/common';

import {
  RateLimiterActionResponse,
  RateLimiterFlushJob,
  RateLimiterFlushJobList,
  RateLimiterFlushStarted,
  RateLimiterMetricsResponse,
  RateLimiterStatusResponse,
} from '../types';
import { AuthService } from './authService';
import { getBearerToken } from './authSession';

const basePath = import.meta.env.REACT_APP_API_ENDPOINT_ENVIRONMENT || '/';

/** BE validates the imsi path param against this exact pattern. */
export const IMSI_PATTERN = /^[0-9]{6,15}$/;

/** 404 = the environment does not exist or belongs to another organisation. */
export const RATE_LIMITER_NOT_FOUND_MESSAGE =
  'Environment not found. Please refresh and try again.';

/** 404 on the job route = unknown job id (e.g. gone after a restart). */
export const FLUSH_JOB_NOT_FOUND_MESSAGE = 'Unblock job no longer exists.';

type RateLimiterParams = {
  environmentId: string;
  imsi: string;
};

const tryParseJson = async (response: Response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

// Does not go through requestWithOptions: its status middleware discards
// non-2xx bodies, but the rate-limiter's 502 body is data — a partial
// failure that still carries per-region outcomes and must reach the UI.
const requestRateLimiter = async <T>(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  { notFoundMessage = RATE_LIMITER_NOT_FOUND_MESSAGE } = {},
): Promise<T> => {
  const currentUserSession = await AuthService.currentSession();
  const response = await fetch(`${basePath}/rate-limiter/${path}`, {
    method,
    headers: {
      Authorization: getBearerToken(currentUserSession),
      Accept: '*/*',
      'Content-Type': 'application/json',
    },
  });

  // Any 2xx body is data: GET/DELETE answer 200, POST 201, and the flush
  // start (DELETE /entries) answers 202 Accepted with the new job's id.
  if (response.ok) {
    return (await response.json()) as T;
  }

  if (response.status === 502) {
    const body = await tryParseJson(response);
    if (body && Array.isArray(body.regions)) {
      return body as T;
    }
    throw new HttpError(INTERNAL_SERVER_ERROR, 502);
  }

  if (response.status === 401) {
    throw new HttpError(UNAUTHORIZED_ERROR, 401, {
      userMessage: UNAUTHORIZED_ERROR,
    });
  }

  if (response.status === 403) {
    throw new HttpError(ACCESS_DENIED_ERROR, 403, {
      userMessage: ACCESS_DENIED_ERROR,
    });
  }

  if (response.status === 404) {
    throw new HttpError(notFoundMessage, 404, { userMessage: notFoundMessage });
  }

  const body = await tryParseJson(response);
  if (body?.detail) {
    const detail = removeQuotesFromText(
      typeof body.detail === 'string'
        ? body.detail
        : JSON.stringify(body.detail),
    );
    // Only a 4xx detail is backend-authored copy for the user (CWE-209).
    throw new HttpError(detail, response.status, {
      userMessage: response.status < 500 ? detail : undefined,
      code: body.error_code,
      errorId: body.error_id,
    });
  }

  throw new HttpError(INTERNAL_SERVER_ERROR, response.status);
};

/** Always 200; a region that can't be read reports its own `error` field. */
export const getImsiRateLimitStatus = async ({
  environmentId,
  imsi,
}: RateLimiterParams) => {
  return requestRateLimiter<RateLimiterStatusResponse>(
    'GET',
    `${environmentId}/${imsi}`,
  );
};

/**
 * Idempotent and safe to retry, but not a no-op: every call resets the
 * counter to the block threshold and refreshes the 24h TTL.
 */
export const blockImsi = async ({ environmentId, imsi }: RateLimiterParams) => {
  return requestRateLimiter<RateLimiterActionResponse>(
    'POST',
    `${environmentId}/${imsi}`,
  );
};

/** Idempotent — unblocking an IMSI that isn't blocked still resolves ok. */
export const unblockImsi = async ({
  environmentId,
  imsi,
}: RateLimiterParams) => {
  return requestRateLimiter<RateLimiterActionResponse>(
    'DELETE',
    `${environmentId}/${imsi}`,
  );
};

/**
 * Starts a flush of ALL rate-limiter entries ("Unblock all IMSIs").
 * Answers 202 Accepted with the new job's id. Repeat calls are safe:
 * while a flush runs they return the running job's id, never a second
 * flush.
 */
export const startRateLimiterFlush = async (environmentId: string) => {
  return requestRateLimiter<RateLimiterFlushStarted>(
    'DELETE',
    `${environmentId}/entries`,
  );
};

/**
 * Blocked-IMSI gauge. Bucket size is server-chosen from the span; limits:
 * span ≤ 150 days, start no older than 150 days (retention).
 */
export const getRateLimiterMetrics = async ({
  environmentId,
  start,
  end,
}: {
  environmentId: string;
  start: Date;
  end: Date;
}) => {
  const params = new URLSearchParams({
    start: start.toISOString(),
    end: end.toISOString(),
  });

  return requestRateLimiter<RateLimiterMetricsResponse>(
    'GET',
    `${environmentId}/metrics?${params.toString()}`,
  );
};

/** Poll every 2 s. 404 = unknown job id. */
export const getRateLimiterFlushJob = async (
  environmentId: string,
  jobId: string,
) => {
  return requestRateLimiter<RateLimiterFlushJob>(
    'GET',
    `${environmentId}/jobs/${jobId}`,
    { notFoundMessage: FLUSH_JOB_NOT_FOUND_MESSAGE },
  );
};

/**
 * In-progress jobs only (today 0 or 1) — for re-attaching after a page
 * reload. Empty also when a flush finished while the page was away.
 */
export const listRateLimiterFlushJobs = async (environmentId: string) => {
  return requestRateLimiter<RateLimiterFlushJobList>(
    'GET',
    `${environmentId}/jobs`,
  );
};
